/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import sprite from '../../../assets/icons/sprite.svg';
import { Badge, DropdownMenu, PaginationInfo, Popup } from '../../../shared';
import { useDebounce, useOnClickOutside } from '../../hooks';
import { InputField } from '../InputField';

import classNames from 'classnames';
import dropdownMenuClasses from '../../components/DropdownMenu/DropdownMenu.module.scss';
import classes from './AutoComplete.module.scss';

const AUTOCOMPLETE_ITEM_HEIGHT_REM = 3;
const DEFAULT_LIST_MAX_HEIGHT_REM = 13.5;

export interface AutoCompleteProps {
  value: any[];
  propToIdentify: string;
  propToDisplay: string;
  fetchQuery: (query: string, page?: number) => any;
  itemTemplate: (item: any) => JSX.Element;
  onChange: (value: any[]) => void;
  onFocus?: () => void;
  nullOptionLabel?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isStatic?: boolean;
  autoFocus?: boolean;
  closeOnSelect?: boolean;
  singleSelect?: boolean;
  listMaxHeightRem?: number;
}

export const AutoComplete: FunctionComponent<AutoCompleteProps> = memo(
  ({
    value,
    propToIdentify,
    propToDisplay,
    itemTemplate,
    fetchQuery,
    onChange,
    onFocus,
    nullOptionLabel,
    placeholder,
    className,
    disabled,
    isStatic,
    autoFocus,
    closeOnSelect,
    singleSelect,
    listMaxHeightRem,
  }) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);

    const [showList, setShowList] = useState<boolean>(false);

    const [list, setList] = useState<any[]>([]);

    const [staticList, setStaticList] = useState<any[]>([]);

    const [pageInfo, setPageInfo] = useState<PaginationInfo>({
      page: 1,
      hasNextPage: false,
    } as PaginationInfo);

    const { page, hasNextPage } = pageInfo;

    const [input, setInput] = useState<string>('');

    const debounced = useDebounce<string>(input);

    const containerRef = useRef<HTMLDivElement>(null);

    const nullOption = useMemo(() => {
      if (!nullOptionLabel || !nullOptionLabel.toLowerCase().includes(debounced.toLowerCase())) {
        return null;
      }

      return { [propToIdentify]: null, [propToDisplay]: nullOptionLabel };
    }, [debounced, nullOptionLabel, propToDisplay, propToIdentify]);

    //EditorJS fix to hide autocomplete list on settings click
    useEffect(() => {
      const editorSettingsClick = () => setShowList(false);

      document
        .querySelector('.ce-toolbar__settings-btn')
        ?.addEventListener('click', editorSettingsClick);

      return () => {
        document
          .querySelector('.ce-toolbar__settings-btn')
          ?.removeEventListener('click', editorSettingsClick);
      };
    }, []);

    useOnClickOutside(containerRef, () => setShowList(false));

    const fetchItems = useCallback(async () => {
      setLoading(true);

      const response = await fetchQuery(debounced).unwrap();

      if (isStatic) {
        setList(response);
        setStaticList(response);
        setLoading(false);
        return;
      }

      const { items = null, pageInfo = null } = { ...response };

      items && setList(items);

      pageInfo && setPageInfo(pageInfo);

      setLoading(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced, fetchQuery]);

    useEffect(() => {
      if (isStatic) {
        setList(
          staticList.filter((item) =>
            item[propToDisplay].toLowerCase().startsWith(debounced.toLowerCase())
          )
        );
        return;
      }

      fetchItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    useEffect(() => {
      if (!isStatic) {
        return;
      }

      fetchItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const itemDelete = useCallback(
      (item: any) => {
        onChange(value.filter((valItem) => valItem[propToIdentify] !== item[propToIdentify]));
      },
      [onChange, propToIdentify, value]
    );

    const itemClick = useCallback(
      (item: any) => {
        if (singleSelect) {
          onChange([item]);
        } else {
          value.find((valItem) => valItem[propToIdentify] === item[propToIdentify])
            ? itemDelete(item)
            : onChange([...value, item]);
        }

        if (singleSelect || closeOnSelect) {
          setShowList(false);
          setInput('');
        }
      },
      [closeOnSelect, itemDelete, onChange, propToIdentify, singleSelect, value]
    );

    const loadMore = useCallback(() => {
      fetchQuery(debounced, page + 1).then(({ data }: any) => {
        setList([...list, ...data.items]);
        setPageInfo(data.pageInfo);
      });
    }, [debounced, fetchQuery, list, page]);

    const renderItem = useCallback(
      (item: any) => {
        const isSelected = value.find(
          (valItem) => valItem[propToIdentify] === item[propToIdentify]
        );

        return (
          <div
            key={item[propToIdentify] ?? item[propToDisplay]}
            onClick={() => itemClick(item)}
            className={classNames(
              classes['autocomplete__list-item'],
              dropdownMenuClasses['dropdown-menu__item']
            )}
          >
            {itemTemplate(item)}
            {isSelected && (
              <svg className={classes['autocomplete__list-item-check']}>
                <use href={`${sprite}#checkmark`} />
              </svg>
            )}
          </div>
        );
      },
      [itemClick, itemTemplate, propToDisplay, propToIdentify, value]
    );

    const renderListItems = useMemo(() => {
      if (loading) {
        return <div className={classes['autocomplete__loading']}>{t('common.loading')}</div>;
      }

      const options = nullOption ? [nullOption, ...list] : list;

      if (!options.length) {
        return <div className={classes['autocomplete__empty']}>{t('common.nothing-found')}</div>;
      }

      const resolvedMaxHeight = listMaxHeightRem ?? DEFAULT_LIST_MAX_HEIGHT_REM;

      const listHeight =
        options.length > Math.floor(resolvedMaxHeight / AUTOCOMPLETE_ITEM_HEIGHT_REM)
          ? `${resolvedMaxHeight}rem`
          : undefined;

      return (
        <InfiniteScroll
          className={classes['autocomplete__infinite-scroll']}
          hasMore={hasNextPage}
          next={loadMore}
          loader={<Skeleton height={'3rem'} />}
          height={listHeight}
          dataLength={options.length}
          hasChildren
        >
          {options.map((item) => renderItem(item))}
        </InfiniteScroll>
      );
    }, [hasNextPage, list, listMaxHeightRem, loadMore, loading, nullOption, renderItem, t]);

    const onFocusHandler = useCallback(() => {
      onFocus?.();
      setShowList(true);
    }, [onFocus]);

    return (
      <div ref={containerRef} className={classNames(classes['autocomplete'], className)}>
        {Boolean(value.length) && (
          <div className={classes['autocomplete__value']}>
            {value.map((item) => (
              <Badge
                key={item[propToDisplay]}
                value={item[propToDisplay]}
                onRemove={() => itemDelete(item)}
                disabled={disabled}
              />
            ))}
          </div>
        )}
        <InputField
          value={input}
          onChange={({ target }) => setInput(target.value)}
          onFocus={onFocusHandler}
          placeholder={placeholder}
          inputClassName={classes['autocomplete__input']}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <Popup
          isOpen={showList}
          bodyTop={'0.5rem'}
          bodyLeft={'0.5rem'}
          bodyWidth={'calc(100% - 1rem)'}
          body={<DropdownMenu content={renderListItems} />}
        />
      </div>
    );
  }
);
